import type { ComponentType, ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { InfoHint } from './InfoHint';

/**
 * Standard page title row. Every page starts with this so titles, subtitles
 * and primary actions sit in the same place at the same size app-wide.
 *
 * Two kinds of text can accompany a title, and they are not interchangeable:
 * `description` EXPLAINS the page and sits behind an info icon; `subline` is
 * DATA about the thing on screen (a supplier's name, today's date) and stays
 * visible under the title.
 */
export function PageHeader({
  title,
  description,
  subline,
  icon: Icon,
  titleAction,
  actions,
  className,
}: {
  title: ReactNode;
  /** What the page is for. Shown from an info icon beside the title. */
  description?: ReactNode;
  /** A fact about the record on screen. Shown under the title. */
  subline?: ReactNode;
  /** Optional leading icon, shown in a tinted tile beside the title. */
  icon?: ComponentType<{ className?: string }>;
  /**
   * One compact control at the end of the title row, as a phone app keeps
   * an icon action in its title bar instead of wrapping it onto a row of its
   * own under the title.
   */
  titleAction?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {Icon && (
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary-50">
            <Icon className="size-5 text-primary-600" />
          </div>
        )}
        <div className="min-w-0 space-y-1">
          {/* Wraps on phones rather than clipping: a title that ends in a count
              badge lost the badge off the edge at 375px. */}
          <div className="flex min-w-0 items-center gap-1.5">
            <h1 className="min-w-0 text-h1 text-foreground sm:truncate">
              {title}
            </h1>
            <InfoHint content={description} />
          </div>
          {subline && (
            // Visible on phones too: a subline is data about the record (an
            // invoice's date and patient), not an explanation to hide.
            <p className="truncate text-small text-muted sm:text-body">
              {subline}
            </p>
          )}
        </div>
        {titleAction && <div className="ms-auto shrink-0">{titleAction}</div>}
      </div>
      {/* Wraps below `sm` so a header with a date picker plus buttons cannot
          push itself off a 375px screen; pinned and non-shrinking from `sm`
          up, where it has the room. Hidden when empty: on a phone a lone
          PageAction is the floating button, portalled out, and the empty box
          still cost a row of gap under the title. */}
      {actions && (
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 empty:hidden sm:flex-nowrap sm:shrink-0">
          {actions}
        </div>
      )}
    </div>
  );
}

/**
 * Search + filters + actions row that sits above a table.
 * Search takes the free space; actions pin to the end.
 */
export function Toolbar({
  search,
  filters,
  actions,
  className,
}: {
  search?: ReactNode;
  filters?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:items-center',
        className,
      )}
    >
      {search && <div className="w-full sm:max-w-xs">{search}</div>}
      {filters && (
        <div className="flex flex-wrap items-center gap-2">{filters}</div>
      )}
      {actions && (
        <div className="flex items-center gap-2 sm:ms-auto">{actions}</div>
      )}
    </div>
  );
}

/**
 * Standard page shell: consistent max-width, gutters and vertical rhythm.
 *
 * The cap lives in one token (`--container-layout`) so every page is the same
 * width — a list, the detail page you reach from it, and the page you navigate
 * to next must not each sit at a different width, or the content jumps
 * sideways on every navigation.
 */
export function PageContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      // No gutters of its own: the shell's content column already pads the
      // page. Padding here as well put these pages 32px further in and 24px
      // lower than every page that does not use the container, so the content
      // jumped on navigation and tables lost 64px they needed on a laptop.
      // A phone gets a tighter rhythm: 32px between every block left a
      // list's first row below the fold.
      className={cn(
        'mx-auto w-full max-w-layout space-y-5 md:space-y-8',
        className,
      )}
    >
      {children}
    </div>
  );
}
